import type { ProviderOutgoingAttachmentType } from "../providerMessagingTypes"

/**
 * ⛔ THE BYTES THAT WILL BE SENT, NOT THE FILE THAT WAS PICKED: an oversized JPEG or PNG is
 * re-encoded before it lands here, so all three fields may belong to the compressor's output rather
 * than to the member's `File` — which already is a `Blob`, so a pass-through pick builds this from it.
 *
 * ⚠ ALL THREE DESCRIBE ONE OBJECT OR NONE OF THEM IS TRUSTWORTHY. `fileName` becomes Healthie's
 * `document_name`, and the BFF re-checks the leading bytes against the DECLARED `contentType` — so a
 * PNG pick compressed to JPEG and still declared `image/png` is a hard 400, which
 * `classifyAttachmentFailure` reads as "file-refused": the member's file thrown away and their own
 * bytes blamed for our mislabelling. The type is resolved once, from the bytes, at the pick.
 */
export type PickedFile = { blob: Blob; fileName: string; contentType: ProviderOutgoingAttachmentType }

/**
 * The composer's ATTACHMENT state, held above the composer (CONAPP-566).
 *
 * ⛔ IT IS HERE FOR THE SAME REASON `draftsByThread` AND `sends` ARE — LIFETIME. THREE things destroy
 * the composer: `ProviderMessagesPanel` swaps it for the closed notice on a conversation that cannot
 * be replied to, the app's tab bar unmounts the WHOLE PANEL for the member's Carrot Companion
 * conversations, and ⚠ the list poll exhausting its retries swaps the entire layout for the error
 * tile — which on a phone happens with no member action at all. The WORDS survive all three, because
 * they are in the host's draft store; a pick held in component state does not. So the member comes
 * back to "photo of the rash attached" in the box, no chip, and a live Send, and one tap posts a note
 * referencing a file that was never sent. Silent omission, which is worse than a dropped pick.
 *
 * ⛔ ALL FIVE PIECES OF PICK STATE ARE HERE, AND THAT IS NOT A TIDINESS DECISION. `pickResolving` is
 * the synchronous backstop refusing a submit while a pick is still being read (see the guard in
 * `onSubmit`), and giving THAT host lifetime on its own would be worse than the bug it half-fixes: it
 * would outlive the pick it guarded, and the remounted composer would refuse sends for a file the
 * member can neither see nor clear. What a conversation is holding, what it is still compressing, why
 * its last offer was refused, which pick it is waiting on and whether one is in flight are one
 * answer.
 *
 * ⛔ AND IT IS OBSERVABLE RATHER THAN A BARE MAP, which is where it differs from `draftsByThread`:
 * the chip, the pending row and the send gate all RENDER from it, and a compression finishes inside a
 * closure belonging to an unmounted composer — so the composer on screen learns about it only by
 * subscribing. This is a `useSyncExternalStore` store, and `announce` is what reaches the live
 * instance. Same shape, and the same argument, as `MessageSendStore`.
 *
 * ⚠ EVERY READER IS PER CONVERSATION BUT ONE. A file is chosen FOR a clinician, and an unsent one
 * riding along into another clinician's conversation is permanent — see `ProviderMessagesPanel`'s
 * composer, which renders one conversation at a time out of this store. `isCompressingAnywhere` is
 * the exception, and says there why memory is the device's question rather than a conversation's.
 *
 * ⚠ MOUNT-SCOPED, NEVER A MODULE CONSTANT. A filename is member-supplied clinical content, so the
 * host builds one per `MessagesShell` mount (see src/pages/messages-shell.tsx) and nothing here
 * survives a sign-out.
 */
export type MessagePickStore = {
  /** React's `useSyncExternalStore` subscription. Returns the unsubscribe. */
  subscribe: (onStoreChange: () => void) => () => void
  /**
   * The file this conversation will send, or null.
   *
   * ⚠ RETURNS THE STORED OBJECT ITSELF, as every reader here does, because `useSyncExternalStore`
   * compares snapshots with `Object.is` — a getter building a fresh value would re-render forever.
   */
  pickedFile: (threadId: string) => PickedFile | null
  /** Why the last file offered in this conversation was not accepted, or null. */
  refusal: (threadId: string) => string | null
  /**
   * The name of the file this conversation is still compressing, or null. ⚠ THE NAME THE MEMBER
   * PICKED: there is no `PickedFile` to read one off until the compressor answers.
   */
  compressingFileName: (threadId: string) => string | null
  /**
   * Whether ANY conversation is compressing, which is what takes the attach control out everywhere
   * (CONAPP-561).
   *
   * ⛔ THE ONE READER HERE THAT IS NOT PER CONVERSATION, and deliberately: a decode and its canvas are
   * tens of megabytes of one DEVICE's memory, not of one conversation's. A member could pick a 25 MB
   * photo in A, move to B and start a second while A's was still decoding, and the peak the rollout
   * switch's comments name is the sum of the two — an OOM kill of the tab, which takes every in-memory
   * draft with it.
   *
   * ⛔ AND IT ANSWERS FOR THE ALLOCATIONS, NOT FOR THE PENDING ROW (CONAPP-568), which is why the two
   * are separate collections below. A superseded pick is off the screen but its bitmap and canvas are
   * still held: a member who picks again in the same conversation while a 25 MiB decode is running
   * gets a second pick that may resolve in milliseconds — a refusal, a pass-through, a small file —
   * and that pick closing used to take the FIRST one's entry with it and re-enable attach everywhere.
   * The summed peak this exists to prevent was then one tap away.
   *
   * ⚠ IT GATES THE ATTACH CONTROL AND NOTHING ELSE. Send stays this conversation's own question (see
   * `isPickResolving`): a compression the member walked away from must not hold a composer they are
   * using somewhere else, and holding a SEND is how a note ends up in the wrong place or not at all.
   */
  isCompressingAnywhere: () => boolean
  /**
   * Whether this conversation has a pick still resolving.
   *
   * ⛔ Called SYNCHRONOUSLY by the submit guard, and that is the whole reason it is separate from
   * `compressingFileName`: `verdictFor` awaits a read of the file's leading bytes with nothing set
   * yet, so there is no pending row for that whole span.
   *
   * ⛔ AND RENDERED FROM AS WELL AS ASKED (CONAPP-568), which is what the guard was missing. Nothing
   * drew this state, so through the byte-sniff Send LOOKED live and a tap on it reached the guard and
   * vanished — no send, no spinner, no status, no refusal, where every other way the composer turns a
   * pick down writes the member a sentence. The composer subscribes to it and holds Send for exactly
   * the span the guard refuses, so the control and the guard can no longer disagree.
   */
  isPickResolving: (threadId: string) => boolean
  /**
   * Opens a pick in this conversation and answers with its token.
   *
   * ⚠ AND IT ANNOUNCES (CONAPP-568), which it deliberately did not while nothing rendered from the
   * in-flight flag. The composer now holds Send off `isPickResolving` — see there for what a guard
   * with no control behind it cost — so the write has to reach the component that draws it.
   */
  beginPick: (threadId: string) => number
  /**
   * Whether this conversation is still interested in that pick — asked everywhere `onPick` resumes
   * from an await.
   *
   * ⚠ ITS OWN CONVERSATION'S QUESTION AND NOBODY ELSE'S: a pick is superseded only by a LATER PICK IN
   * THE SAME CONVERSATION, which is a member choosing another file and watching the composer say so.
   * A pick made somewhere else must not throw away the compression a member walked away from.
   */
  isCurrentPick: (threadId: string, pick: number) => boolean
  /**
   * Closes a pick: releases whatever it was holding, and — when it is still this conversation's
   * current pick — takes the pending row down and lets the conversation submit again.
   *
   * ⛔ THE SCREEN IS A NO-OP FOR A SUPERSEDED PICK, which is what the token is for. A pick the member
   * has since replaced must not take the row belonging to the one that replaced it — that leaves a
   * composer looking ready to send while the file it is still compressing is nowhere on screen.
   * ⚠ NOTHING CAN STICK UNDER THIS RULE: the pick that superseded this one closes itself.
   *
   * ⛔ THE MEMORY IS NOT (CONAPP-568). An allocation belongs to the pick that made it and outlives
   * being superseded, so this releases THIS pick's compression whether or not it is still the current
   * one — and releases no other pick's. Both halves were wrong before: a fast second pick dropped the
   * device-wide attach lock while the first pick's bitmap and canvas were still live, and the first
   * pick, being superseded, then never released anything at all.
   */
  endPick: (threadId: string, pick: number) => void
  /**
   * Draws the pending row: this conversation is waiting on the compressor for that filename, ⚠ AND
   * registers the allocation against the PICK rather than against the conversation (CONAPP-568), so
   * `endPick` can release exactly the one this call made. See `isCompressingAnywhere`.
   */
  startCompressing: (threadId: string, pick: number, fileName: string) => void
  /** The file this conversation will now send. Displaces whatever it was holding, and only there. */
  attach: (threadId: string, file: PickedFile) => void
  /** Takes this conversation's file back off the message. */
  detach: (threadId: string) => void
  /** Why this conversation's last offer was refused. Client-side only; the transport decides again. */
  refuse: (threadId: string, message: string) => void
  /** Takes this conversation's refusal notice down. */
  clearRefusal: (threadId: string) => void
}

export function createMessagePickStore(): MessagePickStore {
  // ⚠ FIVE COLLECTIONS AND NOT ONE RECORD PER THREAD, so a write touches exactly what it means to:
  // a refusal must leave an already-attached file alone (the design pairs the two on screen), and a
  // compression must leave the chip it will replace in place until it answers.
  const picked = new Map<string, PickedFile>()
  const refusals = new Map<string, string>()
  const compressing = new Map<string, string>()
  // Monotonic per conversation, and only ever compared against itself.
  const pickTokens = new Map<string, number>()
  const resolving = new Set<string>()
  // ⛔ THE DEVICE'S LIVE ALLOCATIONS, KEYED BY PICK — which is what separates it from `compressing`
  // above, a conversation's one visible row. Two picks in the SAME conversation can be decoding at
  // once, because nothing cancels the one a member picked over; only the later of them has a row.
  const compressions = new Set<string>()
  const listeners = new Set<() => void>()

  // ⚠ NEVER PARSED, only added, deleted and counted, so the separator has no work to do beyond
  // keeping two picks of one conversation apart. A pick is a number, so no thread id can collide.
  function compressionKey(threadId: string, pick: number): string {
    return `${threadId}#${pick}`
  }

  function announce(): void {
    listeners.forEach((listener) => listener())
  }

  return {
    subscribe: (onStoreChange) => {
      listeners.add(onStoreChange)
      return () => {
        listeners.delete(onStoreChange)
      }
    },
    pickedFile: (threadId) => picked.get(threadId) ?? null,
    refusal: (threadId) => refusals.get(threadId) ?? null,
    compressingFileName: (threadId) => compressing.get(threadId) ?? null,
    isCompressingAnywhere: () => compressions.size > 0,
    isPickResolving: (threadId) => resolving.has(threadId),
    beginPick: (threadId) => {
      const pick = (pickTokens.get(threadId) ?? 0) + 1
      pickTokens.set(threadId, pick)
      resolving.add(threadId)
      announce()
      return pick
    },
    isCurrentPick: (threadId, pick) => pickTokens.get(threadId) === pick,
    endPick: (threadId, pick) => {
      // ⛔ AHEAD OF THE SUPERSEDED CHECK, AND OUTSIDE IT. What this pick allocated is this pick's to
      // release whatever the member has picked since; what is on SCREEN belongs to the current pick.
      const released = compressions.delete(compressionKey(threadId, pick))
      if (pickTokens.get(threadId) !== pick) {
        // ⚠ STILL WORTH ANNOUNCING WHEN IT FREED SOMETHING: this may be the last decode on the
        // device, and the attach control everywhere is waiting on exactly that.
        if (released) announce()
        return
      }
      resolving.delete(threadId)
      compressing.delete(threadId)
      // ⚠ UNCONDITIONALLY NOW. This used to announce only when it took a row down, on the argument
      // that a pass-through pick changed nothing anyone renders — which stopped being true when Send
      // began reading `isPickResolving` (CONAPP-568). The composer holding Send has to be told.
      announce()
    },
    startCompressing: (threadId, pick, fileName) => {
      compressing.set(threadId, fileName)
      compressions.add(compressionKey(threadId, pick))
      announce()
    },
    attach: (threadId, file) => {
      picked.set(threadId, file)
      announce()
    },
    detach: (threadId) => {
      if (picked.delete(threadId)) announce()
    },
    refuse: (threadId, message) => {
      refusals.set(threadId, message)
      announce()
    },
    clearRefusal: (threadId) => {
      if (refusals.delete(threadId)) announce()
    }
  }
}
